import type { ReactNode } from 'react';
import { ColumnHeadContent } from '@immo/components';
import { cn, TableHead } from '@immo/ui-kit';
import { ColumnLabel } from '@/components/layout/page-leading';
import { ARIA_SORT, SortableHeader } from '@/components/sortable-table';
import { useTranslations } from '@/i18n/use-translations';
import type { OperatorColumn } from './operator-column';
import type { OperatorTable } from './use-operator-table';

type OperatorColumnHeadProps<TRow> = {
  readonly column: OperatorColumn<TRow>;
  readonly table: OperatorTable<TRow>;
};

export function OperatorColumnHead<TRow>({ column, table }: OperatorColumnHeadProps<TRow>): ReactNode {
  const t = useTranslations('pages.admin.shell.table');
  const headCellProps = table.columns.headCellProps(column.id);
  const active = table.sort !== null && table.sort.field === column.id;

  return (
    <TableHead
      {...headCellProps}
      className={cn(column.headClassName, headCellProps.className)}
      style={column.headStyle}
      {...(column.headLabelClassName === undefined ? {} : { title: column.label })}
      {...(active ? { 'aria-sort': ARIA_SORT[table.sort.direction] } : {})}
    >
      <ColumnHeadContent
        controls={table.columns.headControls(column.id)}
        onSort={(direction) => table.setSort(column.id, direction)}
      >
        <SortableHeader
          field={column.id}
          label={
            <ColumnLabel
              {...(column.icon === undefined ? {} : { icon: column.icon })}
              {...(column.headLabelClassName === undefined ? {} : { labelClassName: column.headLabelClassName })}
            >
              {column.label}
            </ColumnLabel>
          }
          ariaLabel={t('sort-label', { column: column.label })}
          sort={table.sort}
          onSort={table.toggleSort}
          {...(column.align === undefined ? {} : { align: column.align })}
        />
      </ColumnHeadContent>
    </TableHead>
  );
}
